/* 
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { isStrEmptyOrNull } from '../common/UtilityFunctions';
import { createContext, ReactNode, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAbortController from '../common/hooks/useAbortController';
import axiosCall from '../services/axios';
import { getLoggedOut, removeLoggedOut, setAngelAISessionData } from '../storage/userStorage';
import { useAuthContext } from './AuthContext';
import { useLoginAuditContext } from './LoginAuditContext';

type statusAlertType = {
  status: string;
  message: string;
};

type loginValue = {
  status: string;
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  loadingMessage: string;
  validate: (SyntheticEvent) => void;
  sendOTP: () => void;
  verifyOTP: (SyntheticEvent) => void;
  getSessData: () => void;
  doLogin: (SyntheticEvent) => void;
  logout: () => Promise<void>;

  loginModalOpen: boolean;
  setLoginModalOpen: (boolean) => void;

  emailAddress: string;
  setEmailAddress: (string) => void;
  hiddenEmailAddress: string;
  password: string;
  setPassword: (string) => void;
  otp: string;
  setOtp: (String) => void;
  certified: boolean;
  setCertified: (boolean) => void;
  otpError: boolean;
  setOtpError: (boolean) => void;
  showChooseOTPModal: boolean;
  setShowChooseOTPModal: React.Dispatch<React.SetStateAction<boolean>>;
  authenticationMethod: OTPMethod;
  setAuthenticationMethod: React.Dispatch<React.SetStateAction<OTPMethod>>;
};

const LoginContext = createContext<loginValue | null>(null);

const useLoginContext = () => {
  const loginContext = useContext(LoginContext);
  if (loginContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('createAccountContext must be inside a CreateAccountProvider');
  }
  return loginContext;
};

type loginProviderProps = {
  children: ReactNode;
};

export enum OTPMethod {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
}

const LoginProvider = ({ children }: loginProviderProps): JSX.Element => {
  const [submitStatus, setSubmitStatus] = useState('done');
  const [statusAlert, setStatusAlert] = useState({ status: '', message: '' });

  const [loadingMessage, setLoadingMessage] = useState('');
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [hiddenEmailAddress, setHiddenEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [certified, setCertified] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [authenticationMethod, setAuthenticationMethod] = useState<OTPMethod>(OTPMethod.EMAIL);
  const [showChooseOTPModal, setShowChooseOTPModal] = useState(false);

  const { get } = useAbortController();
  const { email, phoneNo, webUserId, token, logout: authLogout, updateUser, updateBorrowerUser } = useAuthContext();
  const { captureLoginAudit } = useLoginAuditContext();
  const navigate = useNavigate();

  const otpRequest = useMemo(() => {
    if (authenticationMethod == OTPMethod.EMAIL) {
      return {
        authenticationMethod: authenticationMethod,
        emailAddress: emailAddress,
      };
    } else if (authenticationMethod == OTPMethod.PHONE) {
      return {
        authenticationMethod: authenticationMethod,
        emailAddress: emailAddress,
        phoneNumber: phoneNo,
      };
    }
  }, [authenticationMethod, phoneNo, emailAddress]);

  const status = useMemo(() => {
    const statuses = [submitStatus];
    if (statuses.includes('error')) {
      return 'error';
    }
    if (statuses.includes('loading')) {
      return 'loading';
    }
    return 'done';
  }, [submitStatus]);

  const configureHiddenEmailAddress = useCallback(() => {
    const [name, domain] = emailAddress.split('@');
    const splitDomain = domain.split('.');
    const lastDomainField = splitDomain[splitDomain.length - 1];
    const astericks = '*';
    setHiddenEmailAddress(
      name[0] +
        astericks.repeat(name.length - 1) +
        '@' +
        domain[0] +
        astericks.repeat(domain.length - lastDomainField.length - 2) +
        '.' +
        lastDomainField,
    );
  }, [emailAddress]);

  //Function to send OR resend OTP
  const sendOTP = useCallback(async () => {
    setShowChooseOTPModal(false);
    setSubmitStatus('loading');
    // URL and body to Send OTP
    const url = process.env[`REACT_APP_AUTHENTICATION_SERVICE_API`] + 'otp/send';

    try {
      fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(otpRequest),
      });

      await captureLoginAudit({
        eventType: '2FASENT',
        eventResult: 'SUCCESS',
        emailAddress,
        webUserId: webUserId || '',
      });
    } catch (error) {
      await captureLoginAudit({
        eventType: '2FASENT',
        eventResult: 'FAIL',
        failureReason: 'AUTHERR',
        emailAddress,
        webUserId: webUserId || '',
      });

      console.error(error.message);
      setStatusAlert({
        status: 'error',
        message: 'Sorry, we were unable to send your One-Time Pin, please try again soon',
      });
      setSubmitStatus('done');
      return;
    }
    setLoginModalOpen(true);
    setSubmitStatus('done');
  }, [emailAddress, otpRequest, webUserId, get, captureLoginAudit]);

  /**
   * @Reeese TODO: the request is never sent...
   */
  // Function to validate the TABY credentials given by users upon logging in, also makes the OTP modal appear
  const validate = useCallback(
    async (e: SyntheticEvent) => {
      setSubmitStatus('loading');
      setLoadingMessage('');
      // Verifies the user via endpoint
      const request = {
        url: `public/web-user/validate`,
        method: 'post',
        data: {
          emailAddress: `${emailAddress}`,
          password: `${password}`,
        },
      };

      const config = get();
      const response = await axiosCall(request, config);

      if (!response) throw new Error(`No borrowers found for loan number ${'LoanNoHere'}`);
      updateBorrowerUser(response as WebUser);
      // Handling error Cases, either empty or invalid email or password
      if (emailAddress === null || emailAddress === '') {
        setStatusAlert({ status: 'error', message: 'Please enter an email address.' });
      } else if (!response.validId) {
        await captureLoginAudit({
          eventType: 'LOGIN',
          eventResult: 'FAIL',
          failureReason: 'UNKEMAIL',
          emailAddress,
          webUserId: response.webUserId || '',
        });

        setStatusAlert({
          status: 'error',
          message:
            "No account was found with the provided email address. Please verify that the email address is correct and try again. If you have not yet created an account on our new Red Carpet Service portal, please select 'Create New Account' and create an account.",
        });
      } else if (password === null || password === '') {
        setStatusAlert({ status: 'error', message: 'Please enter a password.' });
      } else if (!response.validPassword) {
        await captureLoginAudit({
          eventType: 'LOGIN',
          eventResult: 'FAIL',
          failureReason: 'INVALIDPAS',
          emailAddress,
          webUserId: response.webUserId || '',
        });

        setStatusAlert({
          status: 'error',
          message:
            "The provided password does not match the email address. Please verify your password and try again. If you would like to reset your password, please select 'Forgot Password' and follow the prompts.",
        });
      }

      if (!response.validId || !response.validPassword) {
        setSubmitStatus('error');
        return;
      }

      setStatusAlert({ status: 'success', message: '' });
      configureHiddenEmailAddress();
      if (!response.requireOtp) {
        getSessData();
        return;
      }

      if (isStrEmptyOrNull(response.phoneNo)) {
        sendOTP();
      } else {
        setShowChooseOTPModal(true);
        setSubmitStatus('done');
      }
    },
    [emailAddress, password, get, sendOTP, updateBorrowerUser, captureLoginAudit],
  );

  // Function to get session and collect the data from getting said session to add to the AuthContext
  const getSessData = useCallback(async () => {
    try {
      setLoginModalOpen(false);
      const request = {
        url: 'session',
        method: 'post',
        data: {
          email: emailAddress,
          password: password,
          webUserSession: true,
        },
      };
      const config = get();
      const headers = {
        'Content-Type': 'application/json',
      };
      const response = await axiosCall(request, config, headers);
      if (!response || !response.token || !response.userId)
        throw new Error(`Invalid or incomplete session response for borrower user`);
      // This is where the AuthContext variables are updated based on the above endpoint response
      await updateUser({
        ...response,
        email: emailAddress,
        webUserId: response.webUserUid,
      });

      // Set AngelAI widget Session Storage values immediately after successful borrower login
      setAngelAISessionData(emailAddress, 'borrower', response.token);

      await captureLoginAudit({
        eventType: 'LOGIN',
        eventResult: 'SUCCESS',
        emailAddress,
        webUserId: response.webUserUid || '',
      });

      //use Lambda to get Borrower key
      await fetch(process.env.REACT_APP_GENERATE_BORROWER_KEY_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...response,
          email: emailAddress,
        }),
      })
        .then(
          (keyResponse: Response) => {
            //handle invalid response
            if (!keyResponse.ok) {
              setSubmitStatus('error');
              throw new Error('Error getting key');
            }
            //get JSON out of response
            return keyResponse.json();
          },
          (reason: any) => {
            //catch any error cases
            console.error(reason);
            setSubmitStatus('error');
            throw new Error('Error getting key');
          },
        )
        .then((data: any) => {
          if (data.hashKey) {
            setSubmitStatus('done');
            navigate(`/select-loans?key=${data.hashKey}`, { state: { servicer: false } });
          } else {
            setSubmitStatus('error');
            throw new Error('Error getting key');
          }
        });
    } catch (error) {
      setSubmitStatus('error');
      console.error(`Error getting session for borrower: ${error.message}`);
    }
  }, [emailAddress, password, webUserId, get, updateUser, navigate, captureLoginAudit]);

  // Function to verify OTP from loginModal
  const verifyOTP = useCallback(
    async (e: SyntheticEvent) => {
      const url = process.env[`REACT_APP_AUTHENTICATION_SERVICE_API`] + 'otp/validate';
      const otpVerifyData = {
        emailAddress: emailAddress,
        otpCode: otp,
      };

      // Sending request
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(otpVerifyData),
        });
        if (response.status !== 200) {
          throw new Error('Invalid OTP');
        }

        await captureLoginAudit({
          eventType: '2FASUCC',
          eventResult: 'SUCCESS',
          emailAddress,
          webUserId: webUserId || '',
        });

        setOtpError(false);
        await getSessData();
      } catch (error) {
        await captureLoginAudit({
          eventType: '2FAFAIL',
          eventResult: 'FAIL',
          failureReason: 'INVALIDOTP',
          emailAddress,
          webUserId: webUserId || '',
        });

        setSubmitStatus('error');
        setLoadingMessage('');
        console.error(error.message);
        setOtpError(true);
      }
      setOtp('');
    },
    [emailAddress, otp, webUserId, getSessData, captureLoginAudit],
  );

  const logout = useCallback(async () => {
    await authLogout(async () => {
      await captureLoginAudit({
        eventType: 'LOGOUT',
        eventResult: 'SUCCESS',
        emailAddress: emailAddress || email || '',
        webUserId: webUserId || '',
      });
    });
  }, [authLogout, captureLoginAudit, email, emailAddress, token, webUserId]);

  const doLogin = useCallback(
    async (e) => {
      setSubmitStatus('loading');
      setLoadingMessage('Logging in...');
      if (process.env.NODE_ENV === 'development') {
        setOtp('');
        await getSessData();
      } else {
        verifyOTP(e);
      }
    },
    [getSessData, verifyOTP],
  );

  // if user was logged out, display logged out alert
  useEffect(() => {
    if (getLoggedOut()) setStatusAlert({ status: 'success', message: 'You have been logged out.' });
    removeLoggedOut();
  }, []);

  // need to update emailAddress from authContext if user refreshes and login data is lost
  useEffect(() => {
    if (email !== emailAddress) setEmailAddress(email);
  }, [email]);

  return (
    <LoginContext.Provider
      value={{
        status,
        statusAlert,
        setStatusAlert,
        loadingMessage,
        validate,
        sendOTP,
        verifyOTP,
        getSessData,
        doLogin,
        logout,
        loginModalOpen,
        setLoginModalOpen,
        showChooseOTPModal,
        setShowChooseOTPModal,
        emailAddress,
        setEmailAddress,
        hiddenEmailAddress,
        password,
        setPassword,
        otp,
        setOtp,
        certified,
        setCertified,
        otpError,
        setOtpError,
        authenticationMethod,
        setAuthenticationMethod,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

export { LoginProvider, useLoginContext };
